// Preguntas condicionales del lado de quien RESPONDE (o lee) el cuestionario.
//
// El builder ya sabe anidar (views/questionGroupsStudio/utils/conditional.js), pero ese
// modulo trabaja sobre el estado del builder: ids locales `_id` y opciones en `q.options`.
// El payload que llega a las vistas de resolucion tiene otra forma, asi que no se puede
// reusar tal cual:
//
//   | dato        | builder                   | payload de resolucion         |
//   |-------------|---------------------------|-------------------------------|
//   | id          | `q._id` (`qs736`)         | `q.id` (entero)               |
//   | opciones    | `q.options`               | `q.question_option`           |
//   | destinos    | `option.related_ids` (ids locales) | ver la tabla de abajo |
//
// Y no hay UN payload de resolucion, hay dos, porque son dos endpoints distintos los que
// sirven el cuestionario y cada uno nombra el vinculo a su manera:
//
//   | pantalla                          | endpoint                  | clave con las hijas |
//   |-----------------------------------|---------------------------|---------------------|
//   | responder (`TicketQuestion`, `AddTicket`) | tickets_categories | `related_ids`       |
//   | ver el resultado (`TicketAnswerNew`)      | question_group     | `questions_related` |
//
// Hasta el 2026-09-08 el de `tickets_categories` mandaba solo el escalar COMPAT
// `question_related_id`, que es la PRIMERA hija de la lista: con dos o mas hijas colgando
// de la misma opcion, la segunda en adelante se dibujaba al nivel de la raiz, como
// hermana de su propio padre. Se arreglo emitiendo `related_ids` desde el backend.
// `destinosDe` lee las cuatro formas, asi que una pantalla nueva funciona sin importar
// cual de los dos endpoints use.

import { CHECKBOX, TITLE } from 'constants/app.constant'

/** Las opciones de una pregunta, venga con la clave que venga. Siempre un array. */
export function opcionesDe(question) {
    if (!question) return []
    return question.question_option ?? question.options ?? question.question_options ?? []
}

const aNumero = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : v
}

/**
 * La forma canonica de un id de pregunta, para usar como clave.
 *
 * Existe para que quien construye un Set de ids y quien lo consulta normalicen
 * IGUAL. Con `String(id)` de un lado y esto del otro, un id no canonico
 * (`"01505"`, `" 1505"`) da un miss silencioso: el error queda en `errors`
 * bloqueando el submit, pero no se muestra en ninguna parte.
 */
export function claveDeId(id) {
    return String(aNumero(id))
}

/**
 * Las preguntas que abre esta opcion, en orden y sin repetidos.
 *
 * Combina las cuatro formas en vez de elegir una por precedencia: `related_ids: []` (array
 * vacio, que existe en borradores del builder) haria caer un `Array.isArray` primero y
 * descartaria el escalar que si trae el dato.
 *
 * `questions_related` es la relacion cruda (array de objetos `{id, name, ...}`), y es lo
 * unico que manda `QuestionGroupShowResource`. Sin leerla, la vista de resultado
 * (`TicketAnswerNew`, que pide el cuestionario por `apiGetQuestionGroupById`) dibujaba al
 * nivel de la raiz todas las hijas menos la primera: la primera anidaba por el escalar
 * COMPAT y el resto no llegaba por ninguna de las claves que se leian aca.
 *
 * Es seguro para el builder, que comparte este modulo via `ProbarCuestionario` y
 * `ramasDelBuilder`: al abrir un cuestionario guardado, `cargarCuestionario` deja
 * `questions_related` crudo en la opcion (ids del servidor) junto a un `related_ids` ya
 * traducido a ids locales. Los dos indices que consultan estos destinos estan armados con
 * `_id`, que va prefijado (`qs736`, `os929`), asi que un id del servidor nunca resuelve y
 * se descarta.
 */
export function destinosDe(option) {
    if (!option) return []
    const crudos = [
        ...(Array.isArray(option.related_ids) ? option.related_ids : []),
        ...(Array.isArray(option.related_uids) ? option.related_uids : []),
        ...(Array.isArray(option.questions_related)
            ? option.questions_related.map((q) => (q && typeof q === 'object' ? q.id : q))
            : []),
        ...(option.question_related_id ? [option.question_related_id] : []),
    ]
    const vistos = new Set()
    const salida = []
    for (const id of crudos) {
        if (!id) continue
        const norm = aNumero(id)
        const clave = String(norm)
        if (vistos.has(clave)) continue
        vistos.add(clave)
        salida.push(norm)
    }
    return salida
}

/**
 * Indice de la lista: acceso por id y el set de las que son hijas de otra.
 *
 * `esHija` se calcula sobre la lista que se recibe. Si una hija quedo afuera (por
 * `visible: false`, por ejemplo), su id no esta en `porId` y el destino se ignora en el
 * recorrido, pero tampoco aparece como raiz: no existe en la lista.
 */
export function construirIndice(questions) {
    const lista = questions || []
    const porId = new Map()
    for (const q of lista) porId.set(String(aNumero(q.id)), q)

    const esHija = new Set()
    for (const q of lista) {
        for (const o of opcionesDe(q)) {
            for (const destino of destinosDe(o)) {
                // Solo cuenta como hija si la pregunta destino esta en esta lista.
                // Un destino colgado (hija borrada, o filtrada por visible:false) no
                // puede sacar de las raices a una pregunta que no esta.
                if (porId.has(String(destino))) esHija.add(String(destino))
            }
        }
    }
    return { porId, esHija }
}

/** true si la pregunta solo se muestra cuando se elige cierta opcion. */
export function esCondicional(questions, questionId) {
    return construirIndice(questions).esHija.has(String(aNumero(questionId)))
}

/**
 * Los ids de opcion elegidos para una pregunta, normalizados a array.
 *
 * Tres formas de valor conviven y las tres tienen que resolver igual:
 *  - RADIO / SELECTOR / MENU: un id suelto.
 *  - CHECKBOX en Formik: un STRING con el JSON del array. `QuestionTypeCheckBox` emite
 *    `JSON.stringify(e)` y el onChange lo guarda tal cual con `setFieldValue`, asi que
 *    `values[q.id]` es '[921,923]' y no un array.
 *  - CHECKBOX en localStorage: el array ya parseado (`saveFormInLocalStorage` hace el
 *    JSON.parse antes de guardar). De ahi salen los initialValues al recargar.
 * Ademas el valor viaja por localStorage, asi que un id que salio numero puede volver
 * string: comparar con `===` contra el id de la opcion falla en silencio.
 */
export function elegidasDe(question, valor) {
    if (valor === null || valor === undefined || valor === '') return []
    let bruto = valor
    if (typeof bruto === 'string' && bruto.trim().startsWith('[')) {
        try {
            const parseado = JSON.parse(bruto)
            if (Array.isArray(parseado)) bruto = parseado
        } catch (e) {
            // No era JSON: se trata como un valor suelto.
        }
    }
    const crudos = Array.isArray(bruto) ? bruto : [bruto]
    return crudos.filter((v) => v !== null && v !== undefined && v !== '').map(aNumero)
}

/**
 * Las preguntas que se ven ahora mismo, en el orden en que se responden, con su nivel y
 * su numero jerarquico.
 *
 * Devuelve `{ visibles, meta }`. `meta` va indexado por id (como string) con
 * `{ nivel, numero, opcion }`: nivel 0 para las raices, `numero` tipo '1', '1.1', '1.1.1',
 * y `opcion` con el id de la OPCION que abre esa pregunta, `null` en las raices.
 *
 * `opcion` existe para poder dibujar la hija debajo de la respuesta que la dispara y no
 * despues del bloque entero de opciones, que es lo que necesita la pantalla de probar del
 * builder. El arbol solo dice de que PREGUNTA cuelga cada hija; de cual de sus opciones,
 * no, y eso no se puede recomponer desde afuera cuando dos opciones abren la misma.
 *
 * Las hijas se insertan inmediatamente despues de su padre, aunque en el array original
 * esten en otra posicion. Es deliberado: la relacion padre-hija manda sobre el orden de
 * `priority`, porque una hija lejos de su padre no se entiende. Consecuencia conocida: si
 * una hija estaba fisicamente despues de un TITLE (separador de seccion) y su padre
 * antes, la hija se dibuja en la seccion del padre.
 *
 * Un TITLE nunca es hija (no tiene opciones) y no lleva numero.
 *
 * @param opciones.expandirTodo muestra todas las preguntas sin mirar las respuestas, pero
 *   igual calcula nivel y numero. Es lo que necesita una vista de solo lectura, donde no
 *   hay valores que evaluar y las respuestas ya dicen que rama se recorrio.
 */
export function resolverVisibles(questions, values, { expandirTodo = false } = {}) {
    const lista = questions || []
    const { porId, esHija } = construirIndice(lista)
    const visibles = []
    const meta = {}
    const yaEmitidas = new Set()

    // `rama` es la pila del recorrido actual, no un set global de vistos: un set global
    // tambien suprimiria una pregunta alcanzable por dos caminos distintos, que es un
    // cuestionario valido. La pila corta solo los ciclos reales (A abre B y B abre A),
    // que el backend deja pasar porque solo valida que la referencia exista.
    const bajar = (question, nivel, numero, rama, opcion = null) => {
        const clave = String(aNumero(question.id))
        if (yaEmitidas.has(clave)) return
        yaEmitidas.add(clave)
        visibles.push(question)
        meta[clave] = { nivel, numero, opcion }

        const elegidas = expandirTodo
            ? null
            : elegidasDe(question, values ? values[question.id] : undefined)

        let indiceHija = 0
        for (const opcion of opcionesDe(question)) {
            if (!expandirTodo) {
                const idOpcion = aNumero(opcion.id)
                if (!elegidas.some((e) => String(e) === String(idOpcion))) continue
            }
            for (const destino of destinosDe(opcion)) {
                const claveDestino = String(destino)
                if (rama.has(claveDestino)) continue
                const hija = porId.get(claveDestino)
                if (!hija) continue
                if (yaEmitidas.has(claveDestino)) continue
                indiceHija += 1
                rama.add(claveDestino)
                bajar(
                    hija,
                    nivel + 1,
                    `${numero}.${indiceHija}`,
                    rama,
                    opcion.id != null ? aNumero(opcion.id) : null
                )
                rama.delete(claveDestino)
            }
        }
    }

    // Una pregunta atrapada en un ciclo (A abre B y B abre A) es hija de algo, asi que no
    // entra por el bucle de raices, y tampoco la alcanza nadie: sin esto el cuestionario
    // entero se dibuja vacio. El backend deja pasar el ciclo porque solo valida que la
    // referencia exista, asi que la lista puede llegar asi. Se tratan como raices, en el
    // orden del array: mostrarlas de mas es mucho mejor que perder el cuestionario.
    const alcanzables = new Set()
    const pendientes = lista
        .filter((q) => !esHija.has(String(aNumero(q.id))))
        .map((q) => String(aNumero(q.id)))
    while (pendientes.length) {
        const actual = porId.get(pendientes.pop())
        for (const o of opcionesDe(actual)) {
            for (const destino of destinosDe(o)) {
                const clave = String(destino)
                if (alcanzables.has(clave) || !porId.has(clave)) continue
                alcanzables.add(clave)
                pendientes.push(clave)
            }
        }
    }

    let indiceRaiz = 0
    for (const question of lista) {
        const clave = String(aNumero(question.id))
        if (esHija.has(clave) && alcanzables.has(clave)) continue
        if (question.question_type_id === TITLE) {
            if (yaEmitidas.has(clave)) continue
            yaEmitidas.add(clave)
            visibles.push(question)
            meta[clave] = { nivel: 0, numero: null, opcion: null }
            continue
        }
        indiceRaiz += 1
        bajar(question, 0, String(indiceRaiz), new Set([clave]))
    }

    return { visibles, meta }
}

/**
 * Los ids de las preguntas visibles ahora, como Set de claves canonicas.
 *
 * Consultarlo SIEMPRE con `claveDeId(id)`, nunca con `String(id)`.
 */
export function idsVisibles(questions, values) {
    return new Set(resolverVisibles(questions, values).visibles.map((q) => claveDeId(q.id)))
}

/**
 * Los ids que dejan de verse al pasar de un juego de respuestas a otro.
 *
 * Se calcula por diferencia y no bajando por los descendientes del padre que cambio: en
 * un CHECKBOX el usuario puede destildar una opcion y dejar tildada otra que abre la
 * misma pregunta, y borrar toda la descendencia se llevaria puesta una respuesta que
 * sigue a la vista.
 */
export function idsQueSeOcultan(questions, valuesAntes, valuesDespues) {
    const antes = idsVisibles(questions, valuesAntes)
    const despues = idsVisibles(questions, valuesDespues)
    const salida = []
    for (const id of antes) {
        if (!despues.has(id)) salida.push(id)
    }
    return salida
}

/** true si la pregunta admite mas de una respuesta a la vez. */
export function esMultiple(question) {
    return question?.question_type_id === CHECKBOX
}
